import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Users, Building2, Briefcase, UserCheck, Eye, Search,
  ChevronRight, Clock, CheckCircle2, AlertTriangle, FileText,
  Activity, Layers, X
} from 'lucide-react';

const ROLE_TABS = [
  { key: 'employee', label: 'Employees', icon: Briefcase, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
  { key: 'freelancer', label: 'Freelancers', icon: UserCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  { key: 'vendor', label: 'Vendors', icon: Building2, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
  { key: 'vendor_artist', label: 'Vendor Artists', icon: Users, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/30' },
];

const STATUS_COLORS = {
  completed: 'text-emerald-400 bg-emerald-500/10',
  in_progress: 'text-blue-400 bg-blue-500/10',
  review: 'text-purple-400 bg-purple-500/10',
  not_started: 'text-zinc-400 bg-zinc-700/30',
  blocked: 'text-red-400 bg-red-500/10',
  overdue: 'text-red-400 bg-red-500/10',
};

export default function AdminLiveView() {
  const { api } = useAuth();
  const [userGroups, setUserGroups] = useState(null);
  const [activeTab, setActiveTab] = useState('employee');
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [loadingDash, setLoadingDash] = useState(false);

  useEffect(() => {
    api.get('/admin/live-view/users').then(r => setUserGroups(r.data)).catch(console.error);
  }, [api]);

  const selectUser = async (u) => {
    setSelectedUser(u);
    setLoadingDash(true);
    setDashData(null);
    try {
      const r = await api.get(`/admin/live-view/dashboard/${u.user_id}`);
      setDashData(r.data);
    } catch (e) {
      console.error('Failed to load dashboard:', e);
    }
    setLoadingDash(false);
  };

  const currentUsers = (userGroups?.[activeTab] || []).filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.role_label || '').toLowerCase().includes(q);
  });

  return (
    <div className="w-full max-w-[1600px] mx-auto space-y-6" data-testid="admin-live-view">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight" data-testid="live-view-title">
          Live Dashboard Viewer
        </h1>
        <p className="text-xs text-zinc-500 mt-0.5">View any user's dashboard exactly as they see it — troubleshoot issues in real time</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
        {/* LEFT: User Selector */}
        <div className="space-y-4">
          {/* Role Tabs */}
          <div className="grid grid-cols-2 gap-2" data-testid="role-tabs">
            {ROLE_TABS.map(tab => (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); setSearch(''); }}
                data-testid={`role-tab-${tab.key}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-xs font-heading font-semibold tracking-wide transition-all ${
                  activeTab === tab.key ? tab.bg + ' ' + tab.color : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:border-zinc-600'
                }`}>
                <tab.icon className="w-4 h-4" />
                {tab.label}
                <span className="ml-auto font-mono text-[10px]">{userGroups?.[tab.key]?.length || 0}</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} data-testid="user-search"
              className="w-full h-9 bg-zinc-900 border border-zinc-800 rounded-md pl-9 pr-3 text-xs text-zinc-300 focus:outline-none focus:ring-1 focus:ring-amber-500"
              placeholder="Search by name, email, or role..." />
          </div>

          {/* User List */}
          <div className="space-y-1 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700" data-testid="user-list">
            {!userGroups ? (
              <div className="text-xs text-zinc-600 text-center py-8">Loading users...</div>
            ) : currentUsers.length === 0 ? (
              <div className="text-xs text-zinc-600 text-center py-8">No users in this category</div>
            ) : currentUsers.map(u => (
              <button key={u.user_id} onClick={() => selectUser(u)}
                data-testid={`user-item-${u.user_id}`}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-md text-left transition-all ${
                  selectedUser?.user_id === u.user_id
                    ? 'bg-amber-500/10 border border-amber-500/30'
                    : 'bg-zinc-900 border border-zinc-800 hover:border-zinc-600'
                }`}>
                <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-zinc-300">{(u.full_name || u.email || '?')[0].toUpperCase()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-white truncate">{u.full_name || u.email}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{u.role_label} &middot; {u.email}</p>
                </div>
                <Eye className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT: Dashboard Preview */}
        <div className="min-h-[400px]">
          {!selectedUser ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <Eye className="w-12 h-12 text-zinc-700 mb-4" />
              <p className="text-sm text-zinc-500 font-heading">Select a user to view their dashboard</p>
              <p className="text-[10px] text-zinc-600 mt-1">You'll see exactly what they see — tasks, work orders, and alerts</p>
            </div>
          ) : loadingDash ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-zinc-600 text-sm font-heading">Loading {selectedUser.full_name}'s dashboard...</div>
            </div>
          ) : dashData ? (
            <LiveDashboardPanel data={dashData} user={selectedUser} onClose={() => { setSelectedUser(null); setDashData(null); }} />
          ) : (
            <div className="text-xs text-red-400 text-center py-8">Failed to load dashboard data</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── LIVE DASHBOARD PANEL ─── */
function LiveDashboardPanel({ data, user, onClose }) {
  const dashType = data.dashboard_type;
  const targetUser = data.user || {};
  const tasks = data.tasks || [];
  const taskCounts = data.task_counts || {};
  const workOrders = data.work_orders || [];
  const vendor = data.vendor;
  const projects = data.projects || [];

  const totalTasks = data.total_tasks || 0;
  const completedCount = taskCounts.completed || 0;
  const inProgressCount = taskCounts.in_progress || 0;
  const blockedCount = taskCounts.blocked || 0;
  const reviewCount = taskCounts.review || 0;

  return (
    <div className="space-y-5" data-testid="live-dashboard-panel">
      {/* User Banner */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-amber-500/20 border-2 border-amber-500/40 flex items-center justify-center">
            <span className="text-lg font-heading font-bold text-amber-400">{(targetUser.full_name || '?')[0].toUpperCase()}</span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-heading font-bold text-white">{targetUser.full_name || targetUser.email}</h2>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-semibold uppercase">{dashType}</span>
            </div>
            <p className="text-xs text-zinc-500">{targetUser.role_label} &middot; {targetUser.email}</p>
          </div>
        </div>
        <button onClick={onClose} data-testid="close-live-view" className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center text-zinc-400">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Task Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Tasks', value: totalTasks, color: 'text-white', icon: Layers },
          { label: 'In Progress', value: inProgressCount, color: 'text-blue-400', icon: Activity },
          { label: 'In Review', value: reviewCount, color: 'text-purple-400', icon: Clock },
          { label: 'Completed', value: completedCount, color: 'text-emerald-400', icon: CheckCircle2 },
          { label: 'Blocked', value: blockedCount, color: 'text-red-400', icon: AlertTriangle },
        ].map(card => (
          <div key={card.label} className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid={`summary-${card.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <card.icon className={`w-4 h-4 ${card.color}`} />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{card.label}</span>
            </div>
            <p className={`text-2xl font-heading font-bold ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Work Orders Section */}
      {workOrders.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="live-view-work-orders">
          <h3 className="text-sm font-heading font-bold text-white uppercase tracking-wide mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-500" /> Work Orders ({workOrders.length})
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {workOrders.map(wo => (
              <div key={wo.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
                <div>
                  <span className="text-xs text-white font-semibold">{wo.work_order_no}</span>
                  <span className="text-[10px] text-zinc-500 ml-2">{wo.scope_title || wo.project_name || ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-amber-400">{'\u20B9'}{(wo.total_amount || 0).toLocaleString('en-IN')}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    wo.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
                    wo.status === 'issued' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>{(wo.status || 'draft').charAt(0).toUpperCase() + (wo.status || 'draft').slice(1)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vendor Info */}
      {vendor && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="live-view-vendor-info">
          <h3 className="text-sm font-heading font-bold text-white uppercase tracking-wide mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-amber-500" /> Vendor Info
          </h3>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><span className="text-zinc-500 block">Company</span><span className="text-white">{vendor.company_name || '-'}</span></div>
            <div><span className="text-zinc-500 block">Type</span><span className="text-white">{vendor.entity_type || '-'}</span></div>
            <div><span className="text-zinc-500 block">Status</span><span className="text-white capitalize">{vendor.status || '-'}</span></div>
          </div>
          {projects.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] text-zinc-500 mb-1">Assigned Projects:</p>
              <div className="flex flex-wrap gap-1">
                {projects.map(p => (
                  <span key={p.id} className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">{p.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Task List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="live-view-tasks">
        <h3 className="text-sm font-heading font-bold text-white uppercase tracking-wide mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-500" /> Tasks ({tasks.length})
        </h3>
        {tasks.length === 0 ? (
          <p className="text-xs text-zinc-500 text-center py-6">No tasks assigned to this user</p>
        ) : (
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700">
            {tasks.slice(0, 50).map(t => {
              const statusClass = STATUS_COLORS[t.status] || STATUS_COLORS.not_started;
              return (
                <div key={t.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-3 py-2.5 hover:border-zinc-600 transition-colors"
                  data-testid={`live-task-${t.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${t.status === 'completed' ? 'bg-emerald-500' : t.status === 'in_progress' ? 'bg-blue-500' : t.status === 'blocked' ? 'bg-red-500' : 'bg-zinc-600'}`} />
                    <div className="min-w-0">
                      <p className="text-xs text-white font-medium truncate">{t.name}</p>
                      <p className="text-[10px] text-zinc-600 truncate">{t.task_type || ''} {t.project_name ? `\u00B7 ${t.project_name}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {t.due_date && <span className="text-[10px] text-zinc-600 font-mono">{t.due_date.slice(0, 10)}</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusClass}`}>
                      {(t.status || 'not_started').replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              );
            })}
            {tasks.length > 50 && <p className="text-[10px] text-zinc-600 text-center pt-2">Showing 50 of {tasks.length} tasks</p>}
          </div>
        )}
      </div>

      {/* Employee-specific: Leave & Salary */}
      {data.leave_balance && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
          <h3 className="text-sm font-heading font-bold text-white uppercase tracking-wide mb-3">Leave Balance</h3>
          <div className="grid grid-cols-4 gap-3 text-xs">
            {Object.entries(data.leave_balance.balances || {}).map(([type, bal]) => (
              <div key={type} className="bg-zinc-950 border border-zinc-800 rounded p-3">
                <p className="text-zinc-500 capitalize">{type.replace(/_/g, ' ')}</p>
                <p className="text-lg font-mono text-white">{bal?.remaining ?? '-'}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
